// ============================================
// src/components/PreferencesSection.jsx
// ============================================
// Compact preferences surface for Spark settings.
// Wraps the three preference sub-components (Food, Music, Sports)
// that were built but never mounted before the 29 Apr 2026 audit.
//
// Design intent (per JEzza, 29 Apr 2026):
//   - Light UI — none of these are required. Each sub-component has
//     its own opt-in toggle.
//   - Maia complements: she fills these in over time as she learns
//     a user's tastes through conversation. The user shouldn't feel
//     they MUST sit down and complete all three sections before
//     Spark gets useful.
//   - Saves to Spark backend today via the /api/spark/preferences endpoints.
//     Future migration target: NarbodicaProfile (cross-app unified
//     profile). See claude/roadmap-cards/unify-profile-writes-to-narbodica-profile.md
//     in dating-app-backend for that work.
// ============================================

import React, { useState, useEffect } from 'react';
import { SPARK_URL } from '../apiConfig';
import FoodPreferences from './preferences/FoodPreferences';
import MusicPreferences from './preferences/MusicPreferences';
import SportsPreferences from './preferences/SportsPreferences';

export default function PreferencesSection() {
  const [preferences, setPreferences] = useState({ food: null, music: null, sports: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState('');

  const token = () => localStorage.getItem('accessToken');

  useEffect(() => { fetchPreferences(); }, []);

  async function fetchPreferences() {
    setLoading(true);
    try {
      const res = await fetch(`${SPARK_URL}/preferences`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const data = await res.json();
      // The endpoint may return { preferences: { food, music, sports } } or
      // a flat object — handle both shapes defensively.
      const prefs = data.preferences || data || {};
      setPreferences({
        food:   prefs.food   || null,
        music:  prefs.music  || null,
        sports: prefs.sports || null,
      });
    } catch (e) {
      // Fail soft — empty prefs let user start fresh
    }
    setLoading(false);
  }

  async function savePreference(category, value) {
    setSaving(true);
    try {
      const res = await fetch(`${SPARK_URL}/preferences/${category}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token()}`,
        },
        body: JSON.stringify(value),
      });
      const data = await res.json();
      setPreferences(prev => ({ ...prev, [category]: data.preferences || value }));
      setSavedFlash(category);
      setTimeout(() => setSavedFlash(''), 1500);
    } catch (e) {
      // Silent fail — preference UI keeps the local state, user can retry
    }
    setSaving(false);
  }

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 12 }}>
        Loading…
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Honest framing — no pressure, Maia helps over time */}
      <div style={{
        background: 'rgba(20,184,166,0.06)',
        border: '1px solid rgba(20,184,166,0.18)',
        borderRadius: 10,
        padding: '10px 14px',
        fontSize: 12,
        color: 'var(--text-secondary, #a1a1aa)',
        lineHeight: 1.55,
      }}>
        Optional. Maia also picks these up from conversation over time —
        you don't need to fill anything in now. Toggle on what's useful;
        skip the rest.
      </div>

      <PreferenceBlock
        icon="🍽️"
        label="Food"
        flash={savedFlash === 'food'}
        Component={FoodPreferences}
        prefs={preferences.food}
        onUpdate={(v) => savePreference('food', v)}
        saving={saving}
      />

      <PreferenceBlock
        icon="🎵"
        label="Music"
        flash={savedFlash === 'music'}
        Component={MusicPreferences}
        prefs={preferences.music}
        onUpdate={(v) => savePreference('music', v)}
        saving={saving}
      />

      <PreferenceBlock
        icon="⚽"
        label="Sports"
        flash={savedFlash === 'sports'}
        Component={SportsPreferences}
        prefs={preferences.sports}
        onUpdate={(v) => savePreference('sports', v)}
        saving={saving}
      />
    </div>
  );
}

// Small expandable block — collapsed by default so the section stays
// quiet for users who don't want to fill in preferences. Click to expand,
// click again to collapse. Keeps the settings surface compact.
function PreferenceBlock({ icon, label, flash, Component, prefs, onUpdate, saving }) {
  const [expanded, setExpanded] = useState(false);
  const hasData = prefs && (
    (prefs.enabled) ||
    (prefs.cuisines?.length) ||
    (prefs.genres?.length) ||
    (prefs.types?.length) ||
    (prefs.dietaryRestrictions?.length) ||
    (prefs.artists?.length) ||
    (prefs.teams?.length)
  );

  return (
    <div style={{
      background: 'var(--bg-card, rgba(39,39,42,0.5))',
      border: `1px solid ${flash ? '#14b8a6' : 'var(--border, rgba(255,255,255,0.08))'}`,
      borderRadius: 12,
      transition: 'border-color 0.4s',
    }}>
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'none',
          border: 'none',
          padding: '12px 14px',
          cursor: 'pointer',
          fontSize: 14,
          fontWeight: 600,
          color: 'var(--text-primary, #e4e4e7)',
          fontFamily: 'inherit',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18 }}>{icon}</span>
          <span>{label}</span>
          {hasData && !expanded && (
            <span style={{ fontSize: 10, color: '#14b8a6', fontWeight: 500 }}>· filled in</span>
          )}
          {flash && (
            <span style={{ fontSize: 10, color: '#14b8a6', fontWeight: 500 }}>✓ saved</span>
          )}
        </span>
        <span style={{ fontSize: 12, color: 'var(--text-tertiary, #71717a)', transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
          ▼
        </span>
      </button>
      {expanded && (
        <div style={{ padding: '4px 14px 14px' }}>
          <Component preferences={prefs} onUpdate={onUpdate} />
          {saving && (
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 8 }}>Saving…</div>
          )}
        </div>
      )}
    </div>
  );
}
